import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UserPlus, Loader2, ArrowRight } from "lucide-react";
import { api } from "../lib/api";

export default function AddAccount() {
  const nav = useNavigate();
  const [nc, setNc] = useState("");
  const [pw, setPw] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [otp, setOtp] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const [otpExpText, setOtpExpText] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{10}$/.test(nc)) return toast.error("کد ملی باید ۱۰ رقم باشد");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/accounts", {
        national_code: nc,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        password: pw,
        otp_password: otp,
        enabled,
        refresh_disabled: refreshDisabled,
        otp_expires_at: 0,
        otp_expires_text: otpExpText,
      });
      toast.success(`اکانت ${data.id} افزوده شد`);
      nav("/accounts");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "خطا در افزودن");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl mx-auto" data-testid="add-account-page">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav("/accounts")} className="p-2 rounded-xl hover:bg-slate-100"><ArrowRight size={18} /></button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">افزودن اکانت جدید</h1>
          <p className="text-sm text-slate-500 mt-1">اطلاعات اکانت را وارد کنید</p>
        </div>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200/80 rounded-2xl shadow-sm p-6 md:p-8 space-y-5">
        <div className="flex items-center gap-3 pb-4 border-b border-slate-100">
          <div className="w-12 h-12 rounded-xl bg-teal-50 text-teal-700 border border-teal-200 grid place-items-center">
            <UserPlus size={22} />
          </div>
          <div className="text-sm text-slate-600">اکانت پس از افزودن به‌طور خودکار وارد چرخه رفرش کوکی می‌شود.</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label="نام">
            <input data-testid="add-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} maxLength={100}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
          </FormField>
          <FormField label="نام خانوادگی">
            <input data-testid="add-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} maxLength={100}
              className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
          </FormField>
        </div>

        <FormField label="کد ملی *">
          <input data-testid="add-national-code" value={nc} onChange={(e) => setNc(e.target.value)} placeholder="0000000000" maxLength={10} required
            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all tabular-nums" />
        </FormField>

        <FormField label="رمز عبور *">
          <input data-testid="add-password" value={pw} onChange={(e) => setPw(e.target.value)} required
            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
        </FormField>

        <FormField label="رمز موقت (OTP) *">
          <input data-testid="add-otp" value={otp} onChange={(e) => setOtp(e.target.value)} required
            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
        </FormField>

        <FormField label="تاریخ و ساعت انقضای رمز موقت (متن آزاد)">
          <input data-testid="add-otp-exp" type="text" value={otpExpText} onChange={(e) => setOtpExpText(e.target.value)}
            placeholder="مثلاً: ۱۴۰۳/۰۵/۱۵ ساعت ۱۴:۳۰"
            className="w-full px-4 py-2.5 rounded-xl border border-slate-300 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none transition-all" />
          <p className="text-xs text-slate-400 mt-1">ورودی متنی ساده — بدون تقویم</p>
        </FormField>

        <div className="flex flex-wrap gap-6 pt-2">
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="add-enabled" type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="w-4 h-4 rounded" />
            <span>اکانت فعال باشد</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input data-testid="add-refresh-disabled" type="checkbox" checked={refreshDisabled} onChange={(e) => setRefreshDisabled(e.target.checked)} className="w-4 h-4 rounded" />
            <span>رفرش خودکار غیرفعال</span>
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button type="button" onClick={() => nav("/accounts")} className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700">انصراف</button>
          <button data-testid="add-submit-button" type="submit" disabled={loading}
            className="px-5 py-2.5 rounded-xl bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2 shadow-sm active:scale-[0.98]">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <UserPlus size={16} />}
            افزودن اکانت
          </button>
        </div>
      </form>
    </motion.div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
